import { QueryDiagnosticDeviceData, QueryStatusData } from "@/models/QueryDiagnosticDeviceData";

export const DIAGNOSTIC_ENGINE_SPEED_ID = "DiagnosticEngineSpeedId";
export const DIAGNOSTIC_IGNITION_ID = "DiagnosticIgnitionId";
export const DIAGNOSTIC_ENGINE_ROAD_SPEED_ID = "DiagnosticEngineRoadSpeedId";
export const DIAGNOSTIC_FUEL_LEVEL_ID = "DiagnosticFuelLevelId";
export const DIAGNOSTIC_ADBLUE_LEVEL_ID = "DiagnosticDieselExhaustFluidId";
export const DIAGNOSTIC_SOC_ID = "DiagnosticStateOfChargeId";

/** What the point details on the map can show, beyond the position itself. */
export const ROUTE_DIAGNOSTIC_IDS = [
  DIAGNOSTIC_ENGINE_SPEED_ID,
  DIAGNOSTIC_IGNITION_ID,
  DIAGNOSTIC_ENGINE_ROAD_SPEED_ID,
  DIAGNOSTIC_FUEL_LEVEL_ID,
  DIAGNOSTIC_ADBLUE_LEVEL_ID,
  DIAGNOSTIC_SOC_ID,
];

/**
 * How stale a reading may be before it is no longer said to belong to a position.
 *
 * A vehicle only reports a diagnostic when it changes, so the reading before a position is the
 * one that was true there. Ten minutes on, on a moving vehicle, it is a reading from somewhere
 * else — better to show nothing than the wrong place's fuel level.
 */
export const MAX_DRIFT_MS = 10 * 60 * 1000;

/** The readings that belong to one position on the route. */
export interface MotorValues {
  engineSpeed: number | null;
  /** `null` when nothing was reported, rather than "off". */
  ignition: boolean | null;
  engineRoadSpeed: number | null;
  fuelLevel: number | null;
  adBlueLevel: number | null;
  soc: number | null;
}

export const NO_MOTOR_VALUES: MotorValues = {
  engineSpeed: null,
  ignition: null,
  engineRoadSpeed: null,
  fuelLevel: null,
  adBlueLevel: null,
  soc: null,
};

/**
 * The last reading at or before a moment, if it is recent enough to still apply.
 *
 * Walks the series rather than assuming it arrived in order: the pages come from several
 * requests, and one out-of-order page would make a binary search return the wrong reading.
 *
 * @param entries - One diagnostic's readings for one vehicle.
 * @param time - The moment to read at, as a timestamp.
 * @returns The reading, or `null` if there is none within {@link MAX_DRIFT_MS}.
 */
export function latestValueBefore(
  entries: readonly QueryStatusData[] | undefined,
  time: number,
): number | null {
  if (!entries?.length || !Number.isFinite(time)) {
    return null;
  }

  let bestTime = Number.NEGATIVE_INFINITY;
  let bestValue: number | null = null;

  for (const entry of entries) {
    const entryTime = new Date(entry.dateTime).getTime();
    if (entryTime <= time && entryTime > bestTime) {
      bestTime = entryTime;
      bestValue = entry.data;
    }
  }

  return time - bestTime <= MAX_DRIFT_MS ? bestValue : null;
}

/** Geotab reports ignition as 1 or 0; anything else is not an answer. */
function toIgnition(value: number | null): boolean | null {
  if (value === 1) return true;
  if (value === 0) return false;
  return null;
}

/**
 * What the vehicle was doing at one position on the route.
 *
 * Worked out for a single position rather than for all of them: the readings only ever show for
 * the point a reader clicked, and a day of positions is tens of thousands of records.
 *
 * @param statusData - The readings for every requested diagnostic and vehicle.
 * @param deviceId - Whose position it is.
 * @param time - When the position was recorded, as a timestamp.
 */
export function motorValuesAt(
  statusData: QueryDiagnosticDeviceData | undefined,
  deviceId: string,
  time: number,
): MotorValues {
  if (!statusData) {
    return NO_MOTOR_VALUES;
  }

  const read = (diagnosticId: string) =>
    latestValueBefore(statusData[diagnosticId]?.[deviceId], time);

  return {
    engineSpeed: read(DIAGNOSTIC_ENGINE_SPEED_ID),
    ignition: toIgnition(read(DIAGNOSTIC_IGNITION_ID)),
    engineRoadSpeed: read(DIAGNOSTIC_ENGINE_ROAD_SPEED_ID),
    fuelLevel: read(DIAGNOSTIC_FUEL_LEVEL_ID),
    adBlueLevel: read(DIAGNOSTIC_ADBLUE_LEVEL_ID),
    soc: read(DIAGNOSTIC_SOC_ID),
  };
}
